import dynamic from "next/dynamic";
import { SyntheticEvent, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { datadogRum } from "@datadog/browser-rum";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";

import { useKeyPress } from "@chef/hooks";
import {
  openModal,
  selectIsDebugging,
  showNotification,
  toggleDebugging,
  useGetSanityStatusQuery,
  useLazyMeQuery,
} from "@chef/state-management";
import { useAppDispatch, useAppSelector } from "@chef/state-management/hooks";
import { useFeatureFlagOverrides } from "@chef/hooks";
import { Card } from "@chef/components";

declare const DD_RUM: typeof datadogRum;

const DebugBar = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { overrides } = useFeatureFlagOverrides();

  const [meQuery] = useLazyMeQuery();

  const { data: sanityStatus } = useGetSanityStatusQuery();

  const [agreementId, setAgreementId] = useState("N/A");

  const isDebugging = useAppSelector(selectIsDebugging);

  const handleToggleDebugging = () => {
    dispatch(toggleDebugging());
  };

  useKeyPress("Control+Shift+KeyD", () => {
    handleToggleDebugging();
  });

  useEffect(() => {
    (async () => {
      const agreementId =
        (await meQuery().unwrap())?.agreementId?.toString() || "N/A";

      setAgreementId(agreementId);
    })();
  }, [meQuery, router.route]);

  let viewId = "Datadog not loaded";
  if (DD_RUM) {
    viewId = DD_RUM.getInternalContext()?.view?.id || "No view";
  }

  const handleCopy = (e: SyntheticEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    const target = e.target as HTMLButtonElement;
    const text = target.textContent as string;
    navigator.clipboard.writeText(text);

    // Show a notification
    showNotification({ type: "success", message: "Copied to clipboard!" });
  };

  const handleOpenFeatureFlags = () => {
    dispatch(openModal({ id: "OVERRIDE_FEATURE_FLAGS" }));
  };

  const nActiveOverrides = Object.keys(overrides).length;

  const data = [
    {
      label: "AgreementId",
      value: agreementId,
    },
    {
      label: "DataDog ViewId",
      value: viewId || "Loading...",
    },
    {
      label: "Sanity draft mode",
      value: sanityStatus ? sanityStatus.isPreview.toString() : "Loading...",
      link: sanityStatus?.isPreview
        ? `/api/sanity/exit-preview?redirect=${window.location.pathname}`
        : `/api/sanity/draft?type=landing&secret=preview.secret`,
    },
  ];

  return (
    <Dialog
      open={isDebugging}
      onClose={handleToggleDebugging}
      className="relative z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/35 backdrop-blur-sm" />
      <div className="fixed flex items-center justify-start top-2 left-2 right-2">
        <DialogPanel className="max-w-screen-sm">
          <Card className="relative text-white bg-black" transparent>
            <div className="w-full h-full">
              <p>
                <strong>DEBUG MODE</strong>
              </p>

              <p className="text-sm">
                Press &quot;Control+Shift+D&quot; or tap the Logo in the drawer
                7 times to toggle this
              </p>

              <hr className="my-2 border-grey-2" />

              <dl className="flex flex-col gap-x-8 gap-y-2">
                {data.map((item) => (
                  <div key={item.label} className="flex flex-col">
                    <dt className="inline mr-2 text-sm">
                      <strong>{item.label}:</strong>
                    </dt>
                    <dd className="inline text-sm">
                      {item.link ? (
                        <a href={item.link}>{item.value}</a>
                      ) : (
                        <button
                          onClick={handleCopy}
                          className="text-left hover:underline"
                        >
                          {item.value}
                        </button>
                      )}
                    </dd>
                  </div>
                ))}
                <div className="flex flex-col">
                  <dt className="inline text-sm">
                    <strong>Feature flags:</strong>{" "}
                  </dt>
                  <dd className="inline text-sm">
                    <button
                      onClick={handleOpenFeatureFlags}
                      className="text-left"
                    >
                      {nActiveOverrides} overrides active (
                      <span className="underline">
                        click here to override flags
                      </span>
                      )
                    </button>
                  </dd>
                </div>
              </dl>
            </div>
          </Card>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default dynamic(() => Promise.resolve(DebugBar), {
  ssr: false,
});
